import React from "react"
import pdfIcon from "../assets/doc_icons/pdf.svg"
import imageIcon from "../assets/doc_icons/image.svg"
import docsIcon from "../assets/doc_icons/docx2.svg"
import excelIcon from "../assets/doc_icons/excel.svg"
import videoIcon from "../assets/doc_icons/video.svg"
import documentIcon from "../assets/doc_icons/document.svg"
import imageIcon2 from "../assets/doc_icons/image2.svg"
import filmIcon from "../assets/doc_icons/film.svg"
import Cookies from "js-cookie"
import moment from "moment"
import { Base64 } from 'js-base64';

const delayHoursForView = 24
const startupProfileViewCookie = "startup_profile_last_viewed_at"
const pitchReelViewCookie = "pitch_reel_last_viewed_at"
// A reel feed is scrolled far faster than profiles are opened, so its cookie would
// otherwise run past the browser's ~4KB limit and be dropped wholesale. Cap the
// entries and evict oldest-first; an evicted reel just records one extra view.
const maxViewEntries = 40

const readViews = (cookieName) => {
  try {
    const parsed = JSON.parse(Cookies.get(cookieName) || "{}")
    // Older cookie formats stored a bare timestamp string, not a map.
    return parsed && typeof parsed === "object" && !Array.isArray(parsed) ? parsed : {}
  } catch (e) {
    return {}
  }
}

// Entries are keyed "<investor_id>::<entity_id>" so the throttle is scoped to the
// viewer, not just the thing viewed: two investors sharing a browser each get their
// own window instead of the second one's view being silently dropped.
const viewKey = (investor_id, entity_id) => `${investor_id}::${entity_id}`

const getViewedAt = (cookieName, investor_id, entity_id) =>
  readViews(cookieName)?.[viewKey(investor_id, entity_id)] || null

const setViewedAt = (cookieName, investor_id, entity_id) => {
  // Entries past their throttle window can never suppress a view again, so drop
  // them on write to keep the cookie bounded.
  const fresh = Object.entries(readViews(cookieName)).filter(
    ([, viewedAt]) => moment().diff(moment(viewedAt), 'hours') < delayHoursForView
  )
  fresh.push([viewKey(investor_id, entity_id), new Date().toISOString()])

  const bounded = fresh
    .sort(([, a], [, b]) => moment(a).valueOf() - moment(b).valueOf())
    .slice(-maxViewEntries)

  Cookies.set(cookieName, JSON.stringify(Object.fromEntries(bounded)))
}

// True when this investor has no unexpired view recorded for the entity yet.
const shouldRecordView = (lastViewedAt) =>
  lastViewedAt == null || moment().diff(moment(lastViewedAt), 'hours') >= delayHoursForView

const getLastViewedAt = (investor_id, startup_id) =>
  getViewedAt(startupProfileViewCookie, investor_id, startup_id)

const setLastViewedAt = (investor_id, startup_id) =>
  setViewedAt(startupProfileViewCookie, investor_id, startup_id)

const getReelLastViewedAt = (investor_id, reel_id) =>
  getViewedAt(pitchReelViewCookie, investor_id, reel_id)

const setReelLastViewedAt = (investor_id, reel_id) =>
  setViewedAt(pitchReelViewCookie, investor_id, reel_id)

const domains = {
  "prod": "backrr.com",
  "dev": "bkrr.net",
  "local": "localhost",
}

const domainsForCookies = {
  [domains["prod"]]: "." + domains["prod"],
  [domains["dev"]]: "." + domains["dev"],
  [domains['local']]: domains['local']
};

const subDomains = {
  "investor": "investor",
  "startup": "startup",
  "main": ""
}

const bucketUrls = {
  "prod": "https://investors-prod-pub.s3.ap-south-1.amazonaws.com/investors_media/",
  "dev": "https://investors-dev-pub.s3.ap-south-1.amazonaws.com/investors_media/",
  "local": "https://investors-dev-pub.s3.ap-south-1.amazonaws.com/investors_media/",
}

const startupBucketUrls = {
  "prod": "https://startups-prod-pub.s3.ap-south-1.amazonaws.com/",
  "dev": "https://startups-dev-pub.s3.ap-south-1.amazonaws.com/",
  "local": "https://startups-dev-pub.s3.ap-south-1.amazonaws.com/",
}

const getBucketUrl = () => {
  switch (process.env.REACT_APP_MODE) {
    case "production": return bucketUrls["prod"];
    case "development": return bucketUrls["dev"];
    // default: return bucketUrls["dev"];

  }
}

export const getStartupBucketUrl = () => {
  switch (process.env.REACT_APP_MODE) {
    case "production": return startupBucketUrls["prod"];
    case "development": return startupBucketUrls["dev"];
    // default: return bucketUrls["dev"];

  }
}

function extractMainDomain(url) {
  try {
    // Add protocol if missing
    if (!/^https?:\/\//i.test(url)) {
      url = 'https://' + url;
    }

    const hostname = new URL(url).hostname.replace(/^www\./i, '');
    const parts = hostname.split('.');

    // If only 2 parts like "example.com"
    if (parts.length === 2) {
      return hostname;
    }

    // For domains like "sub.example.co.in", "a.b.c.d.e.site.xyz", etc.
    // Get last 2 parts as fallback domain
    return parts.slice(-2).join('.');
  } catch (e) {
    return null;
  }
}


const dealFlowStatuses = [
  {
    label: 'New',
    value: "invited"
  },
  {
    label: 'Shortlisted',
    value: "interested"
  },
  {
    label: 'Selected',
    value: "in-progress"
  },
  {
    label: 'Rejected',
    value: "rejected"
  }
]

const navigateToDomain = (type = "main", pathname) => {
  let domain = checkForDomain();
  // console.log(domain)
  if (type === "main") {
    if (process.env.REACT_APP_MODE === "production") {
      return "https://" + domains["prod"] + "/" + pathname
    }
    else {
      return "https://" + domains["dev"] + "/" + pathname
    }
    // window.open(domain)
  }
  else if (type === "investor") {
    if (process.env.REACT_APP_MODE === "production") {
      return "https://" + subDomains["investor"] + "." + domains["prod"] + "/" + pathname
    }
    else {
      return "https://" + subDomains["investor"] + "." + domains["dev"] + "/" + pathname
    }
  }
  else if (type === "startup") {
    if (process.env.REACT_APP_MODE === "production") {
      return "https://" + subDomains["startup"] + "." + domains["prod"] + "/" + pathname
    }
    else {
      return "https://" + subDomains["startup"] + "." + domains["dev"] + "/" + pathname
    }
  }
  else {
    if (process.env.REACT_APP_MODE === "production") {
      return "https://" + domains["prod"] + "/" + pathname
    }
    else {
      return "https://" + domains["dev"] + "/" + pathname
    }
  }
  // const resultDomain = type === "main" ? process.env.REACT_APP_MODE === "production" ? String(domain)?.substring(1, String(domain)?.length) : domains["dev"] : domains["dev"];
  // console.log(type, resultDomain,)
  // window.open(resultDomain)
  // window.open(domain?.includes(domains?.prod) ? ("https://" + subDomains[type] + resultDomain) : ("https://" + subDomains[type] + resultDomain))
}

const chartsColorsTheme = [
  "rgba(66, 133, 244, 0.9)",  // Blue
  "rgba(170, 70, 187, 0.9)",  // Purple
  "rgba(15, 157, 88, 0.9)",   // Green
  "rgba(50, 68, 155, 0.9)",   // Red
  "rgba(255, 111, 0, 0.9)",   // Orange
  "rgba(165, 115, 60, 0.9)",  // Brown
  "rgba(255, 204, 0, 0.9)",   // Yellow
  "rgba(255, 0, 255, 0.9)",   // Pink
  "rgba(0, 195, 227, 0.9)"    // Cyan
];

const handleDateValues = (date, placeholder = "No Date") => {
  return date && String(date)?.trim()?.length > 0 ? date : placeholder
}


function getDeviceType() {
  const userAgent = navigator.userAgent || navigator.vendor || window.opera;

  // Check for iOS devices
  if (/iPad|iPhone|iPod/.test(userAgent) && !window.MSStream) {
    return 'ios';
  }

  // Check for Android devices
  if (/android/i.test(userAgent)) {
    return 'android';
  }

  // Default to web-desktop for other cases
  return 'web';
}

export const checkForDomain = () => {
  const domain = window.location.hostname;
  // console.log(domain, "domain"

  // Loop through the keys of the object and check if the domain includes any of them
  for (let key in domainsForCookies) {
    if (domain.includes(key)) {
      return domainsForCookies[key]; // Return the value corresponding to the matching key
    }
  }

  // If no match, return a default value (optional)
  return domain; // Default value
}

const checkForEmail = () => {
  return Cookies.get("email")
}

// this redirect to another portal
function redirectTo(host) {
  switch (process.env.REACT_APP_MODE === "production") {
    case true:
      if (process.env.REACT_APP_PORTAL === "startup") {
        window.open("https://" + subDomains["investor"] + "." + domains["prod"], "_blank");
      }
      else if (process.env.REACT_APP_PORTAL === "investor") {
        window.open("https://" + subDomains["startup"] + "." + domains["prod"], "_blank");
      }
      break;
    default:
      if (process.env.REACT_APP_PORTAL === "startup") {
        window.open("https://" + subDomains["investor"] + "." + domains["dev"], "_blank");
      }
      else if (process.env.REACT_APP_PORTAL === "investor") {
        window.open("https://" + subDomains["startup"] + "." + domains["dev"], "_blank");
      }
      break;
  }
  return null
}

function navigateToMain(host) {
  if (process.env.REACT_APP_MODE === "production") {
    window.location.href = "https://" + domains["prod"]
  }
  else {
    window.location.href = "https://" + domains["dev"]
  }
}

function singleSignOn(host, type) {
  if (process.env.REACT_APP_MODE === "production") {
    if (type === "startup") {
      return "https://" + subDomains["startup"] + "." + domains["prod"]
    }
    else if (type === "investor") {
      return "https://" + subDomains["investor"] + "." + domains["prod"]
    }
    else {
      return null
    }
  }
  else {
    if (type === "startup") {
      return "https://" + subDomains["startup"] + "." + domains["dev"]
    }
    else if (type === "investor") {
      return "https://" + subDomains["investor"] + "." + domains["dev"]
    }
    else {
      return null
    }
  }
}

const getFileIcon = (fileName) => {
  const extension = fileName?.split('.')?.pop()?.toLowerCase();

  switch (extension) {
    case 'pdf':
      return pdfIcon; // Replace with actual path to your PDF icon image
    case 'jpg':
    case 'jpeg':
    case 'png':
    case 'gif':
    case 'bmp':
    case 'svg':
      return imageIcon; // Replace with actual path to your image icon
    case 'doc':
    case 'docx':
      return docsIcon; // Replace with actual path to your DOC/DOCX icon
    case 'xls':
    case 'xlsx':
    case 'csv':
      return excelIcon; // Replace with actual path to your Excel icon
    case 'mp4':
    case 'mov':
    case 'avi':
    case 'mkv':
    case 'wmv':
      return videoIcon; // Replace with actual path to your video icon
    default:
      return docsIcon; // Replace with actual path to a default icon for unsupported types
  }
};

//   // Example usage
//   const fileName = 'example.pdf';
//   const iconPath = getFileIcon(fileName);
//   console.log(iconPath); // Outputs the path to the corresponding icon based on the file extension

// Document/file icons - the exact inline-SVG set from startup-web's getFileCategory
// (indigo #4F46E5 outline on #F4F2FE, rounded), so investor-web's document lists
// match startup-web's. Replaces the earlier getFileIcon delegation, which the senior
// flagged as not matching startup-web's document page.
const svgToDataUri = (svg) => `data:image/svg+xml;utf8,${encodeURIComponent(svg)}`;

const getFileCategory = (fileName) => {
  const extension = fileName?.split('.')?.pop()?.toLowerCase();
  switch (extension) {
    case 'pdf': case 'doc': case 'docx': case 'ppt': case 'pptx': case 'txt':
      return svgToDataUri(`<svg width="40" height="40" viewBox="0 0 40 40" fill="none" xmlns="http://www.w3.org/2000/svg"><rect width="40" height="40" rx="10" fill="#F4F2FE"/><path d="M15.4167 11.75H21.8333L25.5 15.4167V27.3333C25.5 27.5764 25.4034 27.8096 25.2315 27.9815C25.0596 28.1534 24.8264 28.25 24.5833 28.25H15.4167C15.1736 28.25 14.9404 28.1534 14.7685 27.9815C14.5966 27.8096 14.5 27.5764 14.5 27.3333V12.6667C14.5 12.4236 14.5966 12.1904 14.7685 12.0185C14.9404 11.8466 15.1736 11.75 15.4167 11.75Z" stroke="#4F46E5" stroke-width="1.65" stroke-linecap="round" stroke-linejoin="round"/><path d="M21.8334 11.75V15.4167H25.5" stroke="#4F46E5" stroke-width="1.65" stroke-linecap="round" stroke-linejoin="round"/><path d="M17.25 20.9165H22.75M17.25 24.1248H20.9167" stroke="#4F46E5" stroke-width="1.65" stroke-linecap="round" stroke-linejoin="round"/></svg>`);
    case 'csv': case 'xls': case 'xlsx':
      return svgToDataUri(`<svg width="40" height="40" viewBox="0 0 40 40" fill="none" xmlns="http://www.w3.org/2000/svg"><rect width="40" height="40" rx="10" fill="#F4F2FE"/><path d="M25.5 11.75H14.5C13.4874 11.75 12.6666 12.5708 12.6666 13.5833V26.4167C12.6666 27.4292 13.4874 28.25 14.5 28.25H25.5C26.5125 28.25 27.3333 27.4292 27.3333 26.4167V13.5833C27.3333 12.5708 26.5125 11.75 25.5 11.75Z" stroke="#4F46E5" stroke-width="1.65" stroke-linecap="round" stroke-linejoin="round"/><path d="M12.6666 17.25H27.3333M12.6666 22.75H27.3333M18.1666 11.75V28.25" stroke="#4F46E5" stroke-width="1.65" stroke-linecap="round" stroke-linejoin="round"/></svg>`);
    case 'jpg': case 'jpeg': case 'png': case 'gif': case 'bmp': case 'svg':
      return svgToDataUri(`<svg width="40" height="40" viewBox="0 0 40 40" fill="none" xmlns="http://www.w3.org/2000/svg"><rect width="40" height="40" rx="10" fill="#F4F2FE"/><path d="M26.4167 12.6665H13.5833C12.5708 12.6665 11.75 13.4873 11.75 14.4998V25.4998C11.75 26.5124 12.5708 27.3332 13.5833 27.3332H26.4167C27.4292 27.3332 28.25 26.5124 28.25 25.4998V14.4998C28.25 13.4873 27.4292 12.6665 26.4167 12.6665Z" stroke="#4F46E5" stroke-width="1.65" stroke-linecap="round" stroke-linejoin="round"/><path d="M16.7916 19.2666C17.6523 19.2666 18.3499 18.5689 18.3499 17.7082C18.3499 16.8476 17.6523 16.1499 16.7916 16.1499C15.931 16.1499 15.2333 16.8476 15.2333 17.7082C15.2333 18.5689 15.931 19.2666 16.7916 19.2666Z" stroke="#4F46E5" stroke-width="1.65" stroke-linecap="round" stroke-linejoin="round"/><path d="M28.25 23.2085L24.125 19.0835L17.25 25.9585" stroke="#4F46E5" stroke-width="1.65" stroke-linecap="round" stroke-linejoin="round"/></svg>`);
    case 'mp4': case 'mov': case 'avi': case 'mkv': case 'wmv': case 'flv':
      return svgToDataUri(`<svg width="40" height="40" viewBox="0 0 40 40" fill="none" xmlns="http://www.w3.org/2000/svg"><rect width="40" height="40" rx="10" fill="#F4F2FE"/><path d="M26.4167 13.125H13.5833C12.5708 13.125 11.75 13.9458 11.75 14.9583V23.2083C11.75 24.2209 12.5708 25.0417 13.5833 25.0417H26.4167C27.4292 25.0417 28.25 24.2209 28.25 23.2083V14.9583C28.25 13.9458 27.4292 13.125 26.4167 13.125Z" stroke="#4F46E5" stroke-width="1.65" stroke-linecap="round" stroke-linejoin="round"/><path d="M20 25.0415V27.7915M16.7916 27.7915H23.2083" stroke="#4F46E5" stroke-width="1.65" stroke-linecap="round" stroke-linejoin="round"/><path d="M18.625 16.5166L22.2917 18.8999L18.625 21.2833V16.5166Z" stroke="#4F46E5" stroke-width="1.65" stroke-linecap="round" stroke-linejoin="round"/></svg>`);
    default:
      return svgToDataUri(`<svg width="40" height="40" viewBox="0 0 40 40" fill="none" xmlns="http://www.w3.org/2000/svg"><rect width="40" height="40" rx="10" fill="#F4F2FE"/><path d="M15.4167 11.75H21.8333L25.5 15.4167V27.3333C25.5 27.5764 25.4034 27.8096 25.2315 27.9815C25.0596 28.1534 24.8264 28.25 24.5833 28.25H15.4167C15.1736 28.25 14.9404 28.1534 14.7685 27.9815C14.5966 27.8096 14.5 27.5764 14.5 27.3333V12.6667C14.5 12.4236 14.5966 12.1904 14.7685 12.0185C14.9404 11.8466 15.1736 11.75 15.4167 11.75Z" stroke="#4F46E5" stroke-width="1.65" stroke-linecap="round" stroke-linejoin="round"/><path d="M21.8334 11.75V15.4167H25.5" stroke="#4F46E5" stroke-width="1.65" stroke-linecap="round" stroke-linejoin="round"/><path d="M17.25 20.9165H22.75M17.25 24.1248H20.9167" stroke="#4F46E5" stroke-width="1.65" stroke-linecap="round" stroke-linejoin="round"/></svg>`);
  }
};

//   // Example usage
//   const fileName = 'example.mp4';
//   const fileCategory = getFileCategory(fileName);
//   console.log(fileCategory); // Outputs 'video'

const currencyInputProps = {
  inputMode: "numeric", // Use numeric keyboard on mobile devices
  pattern: "[0-9]*", // Only allow digits 0-9
  // title: "Please enter a number between 0 and 9",
  style: {
    // textAlign: 'center', // Center the text in the input
  },
  min: 0,
  onKeyDown: (event) => {
    // Prevent the minus sign (-) from being entered
    if (event.key === "-") {
      event.preventDefault();
    }
  },
  onKeyPress: (event) => {
    // Prevent any input when the minus key is pressed, as it was already prevented in the onKeyDown event
    if (event.key === "-") {
      event.preventDefault();
    }
  },
  onKeyPress: (event) => {
    // Get the input value
    const inputValue = event.key;

    // Allow only numeric characters (0-9) and certain control keys
    const allowedKeys = [
      "0",
      "1",
      "2",
      "3",
      "4",
      "5",
      "6",
      "7",
      "8",
      "9",
      "Backspace",
      "ArrowLeft",
      "ArrowRight",
      "Delete",
      "Tab",
    ];

    // Check if the pressed key is in the allowedKeys array
    if (!allowedKeys.includes(inputValue)) {
      // If not, prevent the key from being entered
      event.preventDefault();
    }
  },
  onInput: (event) => {
    // Get the input value
    const inputValue = event.target.value;

    // Remove non-numeric characters using a regular expression
    const numericValue = inputValue.replace(/[^0-9]/g, "");

    // Update the input field value with the filtered numeric value
    event.target.value = numericValue;
  },
  onKeyDown: (event) => {
    // Prevent the input of minus sign (key code 189)
    if (event.keyCode === 189 || event.key === "-") {
      event.preventDefault();
    }
  },
  disableUnderline: true,
};

// const formatAllowsNegativeCurrency = (value, locale = "en-In") => {
//   if (!value) return "";

//   // Remove invalid characters except numbers, decimal, and negative sign
//   const cleanValue = String(value)?.replace(/[^0-9.-]/g, "");

//   // Ensure only the first `-` is kept and at the start
//   const isNegative = cleanValue.startsWith("-");
//   const numericValue = cleanValue.replace(/-/g, ""); // Remove all `-` signs
//   const sanitizedValue = isNegative ? `-${numericValue}` : numericValue;

//   // Allow only two digits after the decimal point
//   const parts = sanitizedValue.split(".");
//   if (parts.length > 1) {
//     parts[1] = parts[1].slice(0, 2); // Keep only the first 2 digits after '.'
//   }

//   // Join the integer and decimal parts back
//   const truncatedValue = parts.join(".");

//   // Format the integer part with commas
//   const [integerPart, decimalPart] = truncatedValue.split(".");
//   const formattedInteger = parseInt(integerPart || "0", 10).toLocaleString(locale);

//   return decimalPart !== undefined
//     ? `${formattedInteger}.${decimalPart}`
//     : formattedInteger;
// };

const versionInputProps = {
  inputMode: "decimal", // Use numeric keyboard with decimal support on mobile devices
  pattern: "[0-9.]*", // Allow digits 0-9 and '.'
  min: 0,
  onKeyDown: (event) => {
    const { key, target } = event;

    // Prevent the minus sign (-), 'e', or 'E'
    if (key === "-" || key === "e" || key === "E") {
      event.preventDefault();
    }

    // Prevent more than two '.' characters
    if (key === "." && (target.value.match(/\./g)?.length || 0) >= 2) {
      event.preventDefault();
    }
  },
  onInput: (event) => {
    const inputValue = event.target.value;

    // Allow only numbers and periods
    const numericValue = inputValue.replace(/[^0-9.]/g, "");

    // Ensure a maximum of two '.' characters
    const parts = numericValue.split(".");
    const formattedValue = parts.slice(0, 3).join(".");

    // Update the input field value
    event.target.value = formattedValue;
  },
  style: {
    textAlign: "center", // Optional: Center the text
  },
  disableUnderline: true,
};

function versionCheck({ latestVersion: v1, currentVersion: v2 }) {
  // console.log(v1,v2, "before split")
  const arr1 = v1 ? v1?.split('.')?.map(Number) : v2;
  const arr2 = v2?.split('.')?.map(Number);
  // console.log(v1,v2, "after split")

  for (let i = 0; i < Math.max(arr1?.length, arr2?.length); i++) {
    const num1 = arr1[i] || 0; // Default to 0 if undefined
    const num2 = arr2[i] || 0;

    if (num1 > num2) return true;  // v1 is greater
    if (num1 < num2) return false; // v2 is greater
  }
  return false; // Versions are equal
}


const containerRef = (ref) => {
  if (ref) {
    // https://github.com/mdbootstrap/perfect-scrollbar/pull/934/files
    ref._getBoundingClientRect = ref.getBoundingClientRect;

    ref.getBoundingClientRect = () => {
      const original = ref._getBoundingClientRect();

      return {
        ...original,
        width: Math.floor(original.width),
        height: Math.floor(original.height),
      };
    };
  }
}

const isRequired = (requiredObject, field) => {
  return requiredObject[field]?.required
}

const popularCountrySort = ["India", "United States", "United Arab Emirates", "Singapore", "United Kingdom", "Canada"]

// const linkedInTestURL = /^(https?:\/\/)?(www\.)?linkedin\.com\/in\/[a-zA-Z0-9_-]+\/?$/
// const linkedInTestURL = /linkedin\.com\/[a-zA-Z0-9_-]+\/?$/
// const linkedInTestURL = /linkedin\.com\/.*$/
const linkedInTestURL = /.*/


// function stringAvatar(current_name) {
//   let name = (current_name)?.length > 0 ? String(current_name)?.replace(" ", "") : ""
//   if (name && name?.includes(" ")) {
//     return {
//       sx: {
//         bgcolor: "#F9F5FF",
//         color: "#3538CD",
//         width: "40px",
//         height: "40px",
//         fontSize: '1rem',
//         textTransform: 'capitalize'
//       },
//       children: `${name.split(' ')[0][0]}${name.split(' ')[1][0]}`,
//     };
//   } else if (name && !(name?.includes(" "))) {
//     return {
//       sx: {
//         bgcolor: "#F9F5FF",
//         color: "#3538CD",
//         width: "40px",
//         height: "40px",
//         fontSize: '1rem',
//         textTransform: 'capitalize'
//       },
//       children: name[0],
//     };
//   }
//   else {
//     return {
//       sx: {
//         bgcolor: "#F9F5FF",
//         color: "#3538CD",
//         width: "40px",
//         height: "40px",
//         fontSize: '1rem',
//         textTransform: 'capitalize'
//       },
//       children: '',
//     }
//   }
// }

const stringAvatar = (name) => {
  const initials = name
    ? String(name)
      ?.split(' ')
      ?.map((n) => n[0])
      ?.join('')
      ?.toUpperCase()
    : '';
  return {
    sx: {
      bgcolor: "#F9F5FF",
      color: "#3538CD",
      width: "40px",
      height: "40px",
      fontSize: '1rem',
      textTransform: 'capitalize'
    },
    children: (initials)?.slice(0, 2),
  };
};

const capitalizedSentence = (sentence) => sentence && sentence?.length > 0 ? sentence?.split(' ')?.map(word => word?.charAt(0)?.toUpperCase() + word?.slice(1)).join(' ') : '';

// A deal only ever reaches an investor three ways, so the Source column shows exactly
// three labels. The stored values are the internal route names, which leaked into the
// UI as "Pitch Reel" / "Application Form" - display names only, nothing about the data
// changes here:
//   pitch_reel        -> Matched   (the reel is how a matched startup surfaces)
//   application_form  -> Applied
//   matched / invited -> unchanged
// Anything unmapped falls back to the old underscore-to-title-case, so a new source
// added later still reads sensibly instead of disappearing.
const SOURCE_LABELS = {
  pitch_reel: 'Matched',
  application_form: 'Applied',
  matched: 'Matched',
  invited: 'Invited',
  applied: 'Applied',
};

const formatDealSource = (source) => {
  if (!source) return '';
  const key = String(source).trim().toLowerCase();
  if (SOURCE_LABELS[key]) return SOURCE_LABELS[key];
  return String(source).split('_').map((word) => capitalizedSentence(word)).join(' ');
};

const handleCopyClick = (copyLink, copied = false, setCopied) => {
  // Perform the copy action (e.g., copying text to clipboard)
  navigator.clipboard.writeText(copyLink);

  // Change the state to show the clicked (copied) icon
  setCopied(true);

  // Optionally reset the icon back after a delay
  setTimeout(() => setCopied(false), 2000);
};

const interestOptions = (value) => {
  switch (value) {
    case '$': return [
      { label: '+ $1,000', value: "1000" },
      { label: '+ $2,000', value: "2000" },
      { label: '+ $3,000', value: "3000" },
      { label: '+ $5,000', value: "5000" },
      { label: '+ $10,000', value: "10000" },
    ];
    case '₹': return [
      { label: '+ ₹1,00,000', value: "100000" },
      { label: '+ ₹2,00,000', value: "200000" },
      { label: '+ ₹3,00,000', value: "300000" },
      { label: '+ ₹5,00,000', value: "500000" },
      { label: '+ ₹10,00,000', value: "1000000" },
    ];
    default: return [
      { label: '+ ₹1,00,000', value: "100000" },
      { label: '+ ₹2,00,000', value: "200000" },
      { label: '+ ₹3,00,000', value: "300000" },
      { label: '+ ₹5,00,000', value: "500000" },
      { label: '+ ₹10,00,000', value: "1000000" },
    ];
  }
}

const ones = [
  '',
  'One ',
  'Two ',
  'Three ',
  'Four ',
  'Five ',
  'Six ',
  'Seven ',
  'Eight ',
  'Nine ',
  'Ten ',
  'Eleven ',
  'Twelve ',
  'Thirteen ',
  'Fourteen ',
  'Fifteen ',
  'Sixteen ',
  'Seventeen ',
  'Eighteen ',
  'Nineteen '
];
const tens = ['', '', 'Twenty', 'Thirty', 'Forty', 'Fifty', 'Sixty', 'Seventy', 'Eighty', 'Ninety'];

const largeUnits = ['Billion', 'Million', 'Thousand', '']; // Adjust for international units

export const convertToIndianCurrencyWords = (number) => {
  // if (number != undefined) {
  if (typeof number === 'number') {
    number = number.toString();
  }
  if (number === undefined || typeof number !== 'string') {
    return 'Please provide a valid input';
  }
  number = number.replace(/,/g, '');
  const parts = number.split('.');
  let integerPart = parts[0];
  let decimalPart = parts[1];
  number = integerPart;
  // console.log(parts[1], "parts[1]")

  if ((number = number.toString()).length > 13) return '';
  const n = ('0000000000000' + number).substr(-13).match(/^(\d{2})(\d{2})(\d{2})(\d{2})(\d{2})(\d{1})(\d{2})$/);

  if (!n) return '';


  let str = '';
  str += n[1] !== '00' ? (
    (ones[Number(n[1])] || tens[n[1][0]] + ' ' + ones[n[1][1]]) +
    (n[2] === "00" ? 'Thousand Crore ' : 'Thousand ')
  ) : '';


  str += n[2] !== '00' ? (
    (ones[Number(n[2])] || tens[n[2][0]] + ' ' + ones[n[2][1]]) +
    (n[3] === "00" ? 'Hundred Crore ' : 'Hundred ')
  ) : '';

  str += n[3] !== '00' ? (ones[Number(n[3])] || tens[n[3][0]] + ' ' + ones[n[3][1]]) + 'Crore ' : '';
  str += n[4] !== '00' ? (ones[Number(n[4])] || tens[n[4][0]] + ' ' + ones[n[4][1]]) + 'Lakh ' : '';
  str += n[5] !== '00' ? (ones[Number(n[5])] || tens[n[5][0]] + ' ' + ones[n[5][1]]) + 'Thousand ' : '';
  str += n[6] !== '0' ? (ones[Number(n[6])] || tens[n[6][0]] + ' ' + ones[n[6][1]]) + 'Hundred ' : '';
  str += n[7] !== '00' ? (str !== '' ? ' ' : '') + (ones[Number(n[7])] || tens[n[7][0]] + ' ' + ones[n[7][1]]) : '';

  return str.trim() + (decimalPart != "00" && decimalPart != undefined ? " Point " + convertToIndianCurrencyWords(decimalPart) : "") || null;
  // }
  // else{
  //     return ""
  // }
};

export const convertToDollarWords = (number) => {
  if (typeof number === 'number') {
    number = number.toString();
  }
  if (number === undefined || typeof number !== 'string') {
    return 'Please provide a valid input';
  }
  number = number.replace(/,/g, '');
  const parts = number.split('.');
  let integerPart = parts[0];
  let decimalPart = parts[1];
  number = integerPart;

  if (number.length > 12) return ''; // limit to billions

  const n = ('000000000000' + number).substr(-12).match(/^(\d{3})(\d{3})(\d{3})(\d{3})$/);
  if (!n) return '';

  let str = '';
  str += n[1] !== '000' ? convertThreeDigitToWords(n[1]) + 'Billion ' : '';
  str += n[2] !== '000' ? convertThreeDigitToWords(n[2]) + 'Million ' : '';
  str += n[3] !== '000' ? convertThreeDigitToWords(n[3]) + 'Thousand ' : '';
  str += n[4] !== '000' ? convertThreeDigitToWords(n[4]) : '';

  return str.trim() + (decimalPart != "00" && decimalPart !== undefined ? " Point " + convertDecimalToWords(decimalPart) : "") || null;
};

function convertThreeDigitToWords(number) {
  const hundreds = Math.floor(number / 100);
  const remainder = number % 100;
  let str = '';

  if (hundreds > 0) {
    str += ones[hundreds] + 'Hundred ';
  }
  if (remainder > 0) {
    str += remainder < 20 ? ones[remainder] : tens[Math.floor(remainder / 10)] + ones[remainder % 10];
  }
  return str;
}

function convertDecimalToWords(decimalPart) {
  return decimalPart.split('').map(digit => ones[parseInt(digit)]).join('').trim();
}

const amountInWords = (value, currency) => {
  switch (currency) {
    case '$': return convertToDollarWords(value);
    case '₹': return convertToIndianCurrencyWords(value);
    default: return convertToIndianCurrencyWords(value);
  }
}

const scrollToFirstVisibleValidation = () => {
  // Query all elements with the class "validation-message"
  const validationMessages = document.querySelectorAll('.validation-message');
  // console.log(validationMessages, "dddd")

  // Find the first visible validation message
  for (const message of validationMessages) {
    if (message && message.offsetParent !== null) {
      message.scrollIntoView({ behavior: 'smooth', block: 'center' });
      break;
    }
  }
};

const handleFormSubmit = async (e, formik) => {
  e.preventDefault();
  const isValid = await formik.validateForm(); // Validate form before submission

  if (Object.keys(isValid).length > 0) {
    // If validation errors exist, trigger scroll
    formik.setTouched(isValid); // Mark all fields as touched
    setTimeout(() => {
      scrollToFirstVisibleValidation();
    }, 10)
  } else {
    formik.handleSubmit(); // Submit if there are no errors
  }
}

const notEmpty = (value) => {
  return !(value === null || value === undefined || value === '' || (typeof value == "string" && value?.trim()?.length === 0) ||
    (Array.isArray(value) && value.length === 0) ||
    (!(value instanceof Date) && typeof value === 'object' && Object.keys(value).length === 0));
}

function convertCurrencyAmountRange(amount, currency, noofDecimal = 2) {
  let result = '';

  if (amount != null) {
    // Check the currency type
    if (currency === 'INR') {
      if (amount >= 10000000) {
        result = (amount / 10000000).toFixed(noofDecimal) + 'Cr'; // Crores (10 million)
      } else if (amount >= 100000) {
        result = (amount / 100000).toFixed(noofDecimal) + 'L'; // Lakhs (100 thousand)
      } else if (amount >= 1000) {
        result = (amount / 1000).toFixed(noofDecimal) + 'K'; // Thousands
      } else {
        result = amount.toString(); // No conversion for smaller amounts
      }
    } else if (currency) {
      // USD and every other configured currency (EUR, GBP, AED...) use K/M
      if (amount >= 1000000) {
        result = (amount / 1000000).toFixed(noofDecimal) + 'M'; // Millions
      } else if (amount >= 1000) {
        result = (amount / 1000).toFixed(noofDecimal) + 'K'; // Thousands
      } else {
        result = amount.toString(); // No conversion for smaller amounts
      }
    } else {
      result = 'Invalid currency type';
    }
  }

  return result;
}

// Symbols match the `code` values of the investement_amount config
const CURRENCY_SYMBOLS = {
  INR: '₹', USD: '$', EUR: '€', GBP: '£', AED: 'د.إ', SAR: '﷼', SGD: 'S$',
  AUD: 'A$', CAD: 'C$', CHF: 'CHF', JPY: '¥', CNY: '¥', HKD: 'HK$', NZD: 'NZ$',
};

const getCurrencySymbol = (currency, fallback = '') => CURRENCY_SYMBOLS[currency] ?? fallback;

const getINRMarks = () => [
  // { value: 0, label: '< 5L', selectedValue: 0 },
  { value: 0, label: '₹5L', selectedValue: 500000 },
  { value: 1, label: '₹25L', selectedValue: 2500000 },
  { value: 2, label: '₹50L', selectedValue: 5000000 },
  { value: 3, label: '₹2CR', selectedValue: 20000000 },
  { value: 4, label: '₹5CR', selectedValue: 50000000 },
  { value: 5, label: '₹20CR', selectedValue: 200000000 },
  { value: 6, label: '₹50CR +', selectedValue: 500000000 },
];

const getUSDMarks = () => [
  { value: 0, label: '$10K', selectedValue: 10000 },
  { value: 1, label: '$50K', selectedValue: 50000 },
  { value: 2, label: '$100K', selectedValue: 100000 },
  { value: 3, label: '$250K', selectedValue: 250000 },
  { value: 4, label: '$500K', selectedValue: 500000 },
  { value: 5, label: '$1M', selectedValue: 1000000 },
  { value: 6, label: '$5M', selectedValue: 5000000 },
  { value: 7, label: '$10M', selectedValue: 10000000 },
  // { value: 7, label: '> 10M', selectedValue: 10000001 },
];

const getMarks = (currency) => {
  if (currency === 'INR') return getINRMarks(currency)
  const symbol = getCurrencySymbol(currency, '$')
  return getUSDMarks(currency).map(mark => ({ ...mark, label: mark.label.replace('$', symbol) }))
}

const technologyColor = "#FFF8DE"
const consumerBrandColor = "#FFE3E3"
const marketplaceColor = "#F2FFE9"
const servicesColor = "#F5EFFF"
const sectorAgnostic = "#F0EBE3"

const colorArray = [
  "#D4F6FF",
  "#F6EFBD",
  "#FFE3E3",
  "#FFECC8",
  "#EDE8DC",
  "#F5EFFF",
  "#FEF9D9",
  "#F6EACB",
  "#FAF4B7",
  "#FFE6E6",
  "#FFF8DE",
  "#FFE8C5",
  "#F5EEE6",
  "#F2FFE9",
  "#F8E8EE",
  "#F8EDE3",
  "#F0EBE3",
  "#FEF2F4",
]

const isTitleToShow = (title) => {
  return ["Dr.", "CA"].includes(title)
}

// const investor_types = [
//   { label: "Angel", value: "angel" },
//   { label: "Super Angel", value: "super_angel" },
//   { label: "Micro VC", value: "micro_vc" },
//   { label: "VC", value: "vc" },
//   { label: "Family Office", value: "family_office" },
//   { label: "Accelerator", value: "accelerator" },
//   { label: "Incubator", value: "incubator" },
//   { label: "Investment Banker", value: "investment_banker" },
//   {
//     label: "Angel Network / Syndicate", value: "syndicate"
//   }
// ];

const isSelectedInvestorGroup = ({ type, investor_types = [] }) => {
  // console.log(investor_types?.at(6), investor_types?.at(5))
  if (investor_types?.length > 0) {
    switch (type) {
      case investor_types?.at(4)?.value:
        // accelerator
        return true;
      case investor_types?.at(5)?.value:
        // incubator
        return true;
      case investor_types?.at(6)?.value:
        // venture_builder
        return true;
      case investor_types?.at(7)?.value:
        // investment_banker
        return true;
            case investor_types?.at(8)?.value:
        // family_office
        return true;
      case investor_types?.at(9)?.value:
        // syndicate
        return true;
      default:
        return false
    }
  }
  else {
    return false;
  }
}

const websiteTestURL = /^[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
// const downloadFile = (url) => {
//   const link = document.createElement('a');
//   link.href = url;
//   link.target = '_blank';
//   link.setAttribute('download', 'filename.pdf'); // Specify the desired filename here
//   link.click();
// }
const downloadFile = (url, file_name) => {
  fetch(url)
    .then(response => {
      if (!response.ok) {
        throw new Error('Network response was not ok');
      }
      return response.blob(); // Convert the response to a Blob
    })
    .then(blob => {
      const link = document.createElement('a');
      const objectUrl = URL.createObjectURL(blob);

      // Extract the original filename from the URL
      const urlParts = url.split('/');
      const filename = urlParts[urlParts.length - 1] || 'downloaded_file';

      link.href = objectUrl;
      link.setAttribute('download', file_name ? file_name : filename); // Use the extracted filename
      document.body.appendChild(link); // Append the link to the DOM
      link.click(); // Trigger the download
      document.body.removeChild(link); // Clean up the DOM
      URL.revokeObjectURL(objectUrl); // Release the Blob URL
    })
    .catch(error => {
      console.error('Error downloading file:', error);
    });
};

const mergePermissions = ({ fetchedPermissions, permissionsTemplate }) => {
  return permissionsTemplate ? Object.fromEntries(
    Object.entries(permissionsTemplate).map(([module, { label, actions }]) => [
      module,
      {
        label,
        actionLabels: actions, // Keep the action labels as is
        actions: Object.fromEntries(
          Object.keys(actions).map((action) => [
            action,
            fetchedPermissions && fetchedPermissions[module]
              ? fetchedPermissions[module][action] ?? false // Check if fetchedPermissions is valid
              : false  // Default to false if fetchedPermissions is null or empty
          ])
        )
      }
    ])
  ) : {};
};

const defaultPermissions = ({ permissionsTemplate }) => {
  return mergePermissions({ permissionsTemplate: permissionsTemplate, fetchedPermissions: { profile: { view: true } } })
};

const adminDefaultPermissions = ({ permissionsTemplate }) => {
  return Object.entries(defaultPermissions({ permissionsTemplate }))?.reduce((acc, [module, item]) => {
    const updatedActions = Object.keys(item?.actions || {}).reduce((actionsAcc, action) => {
      // console.log(actionsAcc, action)
      actionsAcc[action] = true;
      return actionsAcc;
    }, {});


    acc[module] = {
      ...item,
      actions: updatedActions
    };
    return acc;
  }, {});
}

const collaboratorDefaultPermissions = ({ permissionsTemplate }) => {
  return Object.entries(defaultPermissions({ permissionsTemplate }))?.reduce((acc, [module, item]) => {
    const updatedActions = Object.keys(item?.actions || {}).reduce((actionsAcc, action) => {
      if (module === "profile" && action === "view") {
        actionsAcc[action] = true;
      }
      else {
        actionsAcc[action] = false;
      }

      return actionsAcc;
    }, {});

    acc[module] = {
      ...item,
      actions: updatedActions
    };
    return acc;
  }, {});
}

function convertToPermissionOnly(permissions) {
  const savedPermissions = {}
  Object.keys(permissions)?.map((key) => {
    savedPermissions[key] = permissions[key]["actions"]
  });
  return savedPermissions
}

const adminPermission = ({ permissionsTemplate }) => {
  return convertToPermissionOnly(adminDefaultPermissions({ permissionsTemplate }))
};

/**
 * Get permission value for a given module and key.
 * 
 * @param {"profile"|"startups"|"pitch_reels"|"startups_new"|"portfolio"|"reports"|"settings"} module - The name of the module.
 * @param {string} key - The permission key within the module.
 * @param {Object} permissions - The permissions object.
 * @returns {boolean} - The permission value or false if not found.
 */
function hasPermission(module, key, permissions) {
  // console.log(permissions, module, key, "permissions")
  // if (module === "startups_new" && key === "edit") {
  //   console.log(permissions, "ello", key in permissions[module], permissions[module][key])
  // }
  // if (module === "startups_new" && key === "edit") {
  //   console.log(permissions, permissions[module], "permissions")
  // }

  return permissions && permissions[module] && key in permissions[module]
    ? permissions[module][key]
    : false;  // Default for non-existing keys
}


/**
 * Check if a user has permission for a specific route.
 * 
 * @param {Object} userPermissions - An object containing user permissions for various modules.
 * @param {string} route - The route to check permission for.
 * @returns {boolean} - True if the user has permission for the route, false otherwise.
 */
function hasPermissionForRoute(userPermissions, route) {
  if (route?.includes("bulk-analysis") || route?.includes("orders-invoices")) {
    return true;
  }
  // const backrrPermissionCookie = Cookies.get("permissions") || null;
  // if (route?.includes("claim-profile")) {
  //   return true;
  // }
  // if (route?.includes("backrr_startups") && backrrPermissionCookie && JSON.parse(backrrPermissionCookie)?.backrr_startups) {
  //   return true;
  // }
  // if (route?.includes("backrr_investors") && backrrPermissionCookie && JSON.parse(backrrPermissionCookie)?.backrr_investors) {
  //   return true;
  // }
  // if (route?.includes("admin_settings") && backrrPermissionCookie && JSON.parse(backrrPermissionCookie)?.backrr_admin_settings) {
  //   return true;
  // }
  if (!userPermissions || Object.keys(userPermissions).length === 0) {
    return false;  // Handle null, undefined, or empty userPermissions
  }

  const routeToModuleMap = {
    '/': { module: 'startups', key: 'view' },
    '/deal-flow': { module: 'startups', key: 'view' },
    '/deal-flow/view-profile': { module: 'startups', key: 'view' },
    '/deal-flow/view-round': { module: 'startups', key: 'view' },
    '/deal-flow/view-documents': { module: 'startups', key: 'view' },
    '/deal-flow/view-funding': { module: 'startups', key: 'view' },
    '/deal-flow/view-reports': { module: 'startups', key: 'view' },
    '/deal-flow/view-performance': { module: 'startups', key: 'view' },
    '/deal-flow/view-feedback': { module: 'startups', key: 'submit_feedback' },
    '/deal-flow/view-notes': { module: 'startups', key: 'manage_internal_notes' },
    '/pitch-reels': { module: 'pitch_reels', key: 'view' },
    '/pitch-reels/selected': { module: 'pitch_reels', key: 'view' },
    '/pitch-reels/deal-flow': { module: 'pitch_reels', key: 'view' },
    '/pitch-reels/preference': { module: 'pitch_reels', key: 'view' },
    '/pitch-reels/all': { module: 'pitch_reels', key: 'view' },
    '/startups': { module: 'startups_new', key: 'view' },
    '/startups/add': { module: 'startups_new', key: 'add' },
    '/startups/edit': { module: 'startups_new', key: 'edit' },
    '/startups/rounds/view-round': { module: 'startups_new', key: 'view' },
    '/startups/rounds/add-round': { module: 'startups_new', key: 'edit' },
    '/startups/rounds/edit-round': { module: 'startups_new', key: 'edit' },
    '/startups/rounds/view': { module: 'startups_new', key: 'view' },
    '/startups/rounds/view-investors': { module: 'startups_new', key: 'view' },
    '/startups/rounds/view-documents': { module: 'startups_new', key: 'view' },
    '/startups/view-profile': { module: 'startups_new', key: 'view' },
    '/startups/view-round': { module: 'startups_new', key: 'view' },
    '/startups/view-documents': { module: 'startups_new', key: 'view' },
    '/startups/rounds': { module: 'startups_new', key: 'view' },
    '/startups/reports': { module: 'startups_new', key: 'view' },
    '/startups/reports/add-report': { module: 'startups_new', key: 'view' },
    '/startups/reports/edit-report': { module: 'startups_new', key: 'view' },
    '/startups/performance': { module: 'startups_new', key: 'view' },
    '/startups/performance/add-performance': { module: 'startups_new', key: 'add' },
    '/startups/performance/edit-performance': { module: 'startups_new', key: 'edit' },
    '/startups/users': { module: 'startups_new', key: 'edit' },
    '/startups/users/add-user': { module: 'startups_new', key: 'edit' },
    '/startups/users/edit-user': { module: 'startups_new', key: 'edit' },
    '/startups/view-feedback': { module: 'startups_new', key: 'view' },
    '/startups/email-logs': { module: 'startups_new', key: 'view' },
    '/startups/profile-views': { module: 'startups_new', key: 'view' },
    '/startups/matched-investors': { module: 'startups_new', key: 'view' },
    // '/startups/find-investors': { module: 'startups_new', key: 'view' },
    '/startups/view-notes': { module: 'startups_new', key: 'view' },
    // '/startups/view-feedback': { module: 'startups_new', key: 'submit_feedback' },
    // '/startups/view-notes': { module: 'startups_new', key: 'manage_internal_notes' },
    '/profile': { module: 'profile', key: 'view' },
    '/profile/edit_profile': { module: 'profile', key: 'edit' },
    '/portfolio': { module: 'portfolio', key: 'view' },
    '/portfolio/create_portfolio': { module: 'portfolio', key: 'add' },
    '/portfolio/create_portfolio': { module: 'portfolio', key: 'edit' },
    '/reports': { module: 'reports', key: 'view' },
    '/reports/view': { module: 'reports', key: 'view' },
    '/settings': { module: 'settings', key: 'manage_users' },
    '/settings/add_user': { module: 'settings', key: 'manage_users' },
  };

  const routeInfo = routeToModuleMap[route];

  if (!routeInfo) {
    return false;  // Route not mapped
  }

  const { module, key } = routeInfo;
  return hasPermission(module, key, userPermissions);
}

function findFirstTrueValue(permissions) {
  for (const module in permissions) {
    const keys = permissions[module];
    for (const key in keys) {
      if (keys[key] === true) {
        return { module, key };  // Return the first match found
      }
    }
  }
  return null;  // Return null if no true value is found
}

function getFirstRoute({ module, key }) {
  switch (module) {
    case 'profile':
      return `/`;
    case 'funding_rounds':
      return `/rounds`
    case 'reports':
      return `/reports`
    case 'performance':
      return `/performance`
    case 'find_investors':
      return `/find-investors`
    case 'settings':
      return `/settings`
    default:
      return '/'
  }
}

// function hasPermissionForRoute(userPermissions, route) {
//   if (!userPermissions || Object.keys(userPermissions).length === 0) {
//     return false;  // Handle null, undefined, or empty userPermissions
//   }

//   const routeToModuleMap = {
//     '/': 'profile',
//     '/update': 'profile',
//     '/rounds': 'funding_rounds',
//     '/reports': 'reports',
//     '/performance': 'performance',
//     '/find-investors': 'find_investors',
//     '/settings': 'settings',
//   };

//   const module = routeToModuleMap[route];

//   if (!module || !userPermissions[module]) {
//     return false;  // Route not mapped or no permissions for the module
//   }

//   const permissions = userPermissions[module];
//   console.log(userPermissions, route, module)
//   return Object.values(permissions).some(permission => permission === true);
// }

const formatCurrency = (value, locale = "en-In") => {
  if (!value) return "";

  // Remove invalid characters except numbers and decimal
  const cleanValue = String(value)?.replace(/[^0-9.]/g, "");

  // Allow only two digits after the decimal point
  const parts = cleanValue?.split(".");
  if (parts?.length > 1) {
    parts[1] = parts[1]?.slice(0, 2); // Keep only first 2 digits after '.'
  }

  // Join the integer and decimal parts back
  const truncatedValue = parts?.join(".");

  // Format the integer part with commas
  const [integerPart, decimalPart] = truncatedValue?.split(".");
  const formattedInteger = parseInt(integerPart || "0", 10)?.toLocaleString(locale);

  return decimalPart !== undefined
    ? `${formattedInteger}.${decimalPart}`
    : formattedInteger;
};

const formatDecimal = (value) => {
  if (!value) return "";

  // Remove invalid characters except numbers and decimal
  const cleanValue = String(value)?.replace(/[^0-9.]/g, "");

  // Allow only two digits after the decimal point
  const parts = cleanValue.split(".");
  if (parts.length > 1) {
    parts[1] = parts[1].slice(0, 2); // Keep only the first 2 digits after '.'
  }

  // Join the integer and decimal parts back
  return parts.join(".");
};

const currencyAllowNegativeInputProps = {
  inputMode: "decimal", // Use numeric keyboard with decimal support on mobile devices
  pattern: "-?[0-9.]*", // Allow digits 0-9, '.', and '-'
  onKeyDown: (event) => {
    const { key, target } = event;

    // Prevent invalid characters: 'e', 'E', multiple '-' or '.'
    if (key === "e" || key === "E") {
      event.preventDefault();
    }

    // Prevent multiple '-' (only one allowed at the start)
    if (key === "-" && target.selectionStart !== 0) {
      event.preventDefault();
    }

    // Prevent multiple '.' characters
    if (key === "." && target.value.includes(".")) {
      event.preventDefault();
    }
  },
  onInput: (event) => {
    // Get the input value
    const inputValue = event.target.value;

    // Remove invalid characters except numbers, '.' and '-'
    let numericValue = inputValue.replace(/[^0-9.-]/g, "");

    // Ensure only the first '-' is kept and at the start
    const isNegative = numericValue.startsWith("-");
    numericValue = numericValue.replace(/-/g, ""); // Remove all '-'
    if (isNegative) numericValue = `-${numericValue}`;

    // Ensure only one '.' is present
    const parts = numericValue.split(".");
    if (parts.length > 1) {
      numericValue = `${parts[0]}.${parts[1]}`;
    }

    // Update the input field value
    event.target.value = numericValue;
  },
  style: {
    textAlign: "center", // Optional: Center the text
  },
  disableUnderline: true,
};


const formatAllowsNegativeCurrency = (value, locale = "en-In") => {
  if (!value) return "";

  // Remove invalid characters except numbers, decimal, and negative sign
  const cleanValue = String(value)?.replace(/[^0-9.-]/g, "");

  // Ensure only the first `-` is kept and at the start
  const isNegative = cleanValue.startsWith("-");
  const numericValue = cleanValue.replace(/-/g, ""); // Remove all `-` signs
  const sanitizedValue = isNegative ? `-${numericValue}` : numericValue;

  // Allow only two digits after the decimal point
  const parts = sanitizedValue.split(".");
  if (parts.length > 1) {
    parts[1] = parts[1].slice(0, 2); // Keep only the first 2 digits after '.'
  }

  // Join the integer and decimal parts back
  const truncatedValue = parts.join(".");

  // Format the integer part with commas
  const [integerPart, decimalPart] = truncatedValue.split(".");
  const formattedInteger = parseInt(integerPart || "0", 10).toLocaleString(locale);

  return decimalPart !== undefined
    ? `${formattedInteger}.${decimalPart}`
    : formattedInteger;
};

const formatAllowsNegativeDecimal = (value) => {
  if (!value) return "";

  // Remove invalid characters except numbers, decimal, and negative sign
  const cleanValue = String(value)?.replace(/[^0-9.-]/g, "");

  // Ensure only the first `-` is kept and at the start
  const isNegative = cleanValue.startsWith("-");
  const numericValue = cleanValue.replace(/-/g, ""); // Remove all `-` signs
  const sanitizedValue = isNegative ? `-${numericValue}` : numericValue;

  // Allow only two digits after the decimal point
  const parts = sanitizedValue.split(".");
  if (parts.length > 1) {
    parts[1] = parts[1].slice(0, 2); // Keep only the first 2 digits after '.'
  }

  // Join the integer and decimal parts back
  return parts.join(".");
};

const cookiesInUse = {
  'JWT': 'X-INV-JWT',
  'USER-TYPE': 'X-INV-USER-TYPE',
  'INVESTOR-ID': 'X-INV-ID',
}

const checkForUserType = () => {
  return Cookies.get(cookiesInUse["USER-TYPE"]);
}

const setUserType = (value, domain) => {
  if (domain) {
    return Cookies.set(cookiesInUse["USER-TYPE"], value, { domain: domain });
  }
  else {
    return Cookies.set(cookiesInUse["USER-TYPE"], value);
  }
}

const checkForAuthorization = () => {
  return Cookies.get(cookiesInUse["JWT"]);
}

const setTokenForAuthorization = (token, domain) => {
  // console.log(domain, "dddd")
  if (domain) {
    return Cookies.set(cookiesInUse["JWT"], token, { domain: domain });
  }
  else {
    return Cookies.set(cookiesInUse["JWT"], token);
  }
}

export const setInvestorID = (value, domain) => {
  if (domain) {
    return Cookies.set(cookiesInUse["INVESTOR-ID"], value, { domain: domain });
  }
  else {
    return Cookies.set(cookiesInUse["INVESTOR-ID"], value);
  }
}

export const getInvestorID = () => {
  return Cookies.get(cookiesInUse["INVESTOR-ID"]);
}

function getTemplateKeyValue({ module, key = "label", template }) {
  // majorly used for getting labels from template.
  return template && template[module] && key in template[module]
    ? template[module][key]
    : "";  // Default for non-existing keys
}

const requiredProgressFields = [
  "logo_url", "entity_name", "formation_date", "stage_id", "category_id", "sector_id",
  "business_model_id", "problem_solving", "startup_overview", "website_url", "linkedin_url",
  "founder_details", "pitch_deck_url"
];

const progressLabels = {
  logo_url: "Logo",
  entity_name: "Legal Entity Name",
  formation_date: "Formation Date",
  stage_id: "Stage",
  sector_id: "Sector",
  category_id: "Category",
  business_model_id: "Business Model",
  problem_solving: "Problem Overview",
  description: "Solution Overview",
  website_url: "Website URL",
  linkedin_url: "LinkedIn Page",
  founder: "Founder Details",
  pitch_deck_url: "Pitch Deck",
  elevator_pitch: "Elevator Pitch Video",
  // round_name: "Round Name",
  // round_type: "Round Type",
  // security_type: "Security Type",
  // total_round_size: "Total Round Size",
  // round_valuation: "Round Valuation",
  // round_highlights: "Round Highlights",
  // key_metrics: "Key Metrics",
  documents: "Documents"
};

const roundNavigationFields = [
  "Round Name & Type", "Round Size & Valuation", "Fund Utilization", "Current Traction", "Documents", "Exit Strategy"
]

const getProgressLabel = (key) => progressLabels[key] ?? key;

const progressFieldsRoute = {
  Logo: "/update?page=0",
  "Legal Entity Name": "/update?page=0",
  "Formation Date": "/update?page=0",
  Stage: "/update?page=0",
  Sector: "/update?page=0",
  Category: "/update?page=0",
  "Business Model": "/update?page=0",
  "Problem Overview": "/update?page=2",
  "Solution Overview": "/update?page=2",
  "Website URL": "/update?page=1",
  "LinkedIn Page": "/update?page=1",
  "Founder Details": "/update?page=3",
  "Pitch Deck": "/update?page=2",
  "Elevator Pitch Video": "/update?page=2",
  "Round Name & Type": "/rounds/edit_round?page=0",
  "Round Size & Valuation": "/rounds/edit_round?page=1",
  "Fund Utilization": "/rounds/edit_round?page=3",
  "Current Traction": "/rounds/edit_round?page=4",
  Documents: "/rounds/edit_round?page=5",
  "Exit Strategy": "/rounds/edit_round?page=7",
  "Performance": "/performance"
};

const getProgressFieldRoute = (key) => progressFieldsRoute[key];

const formatProgressData = ({ response }) => {
  if (!response?.data) return { completion: 0, dataWithLabels: [], requiredFieldsLabels: [], dataWithLabelsObject: {} };

  const { completion, data } = response;
  const {
    round_id,
    round_name, round_type, security_type,
    total_round_size, round_valuation, received_termsheet, round_highlights,
    fund_utilization_highlights, key_metrics,
    traction_highlights,
    exit_highlights,
    documents,
    performanceCompletion,
    performance,
    ...rest
  } = data;

  const dataWithLabels = [
    ...Object.entries(rest).map(([key, value]) => ({ [getProgressLabel(key)]: value })),
    { "Round Name & Type": Boolean(round_name && round_type && security_type) && { round_name, round_type, security_type } },
    { "Round Size & Valuation": Boolean(total_round_size && round_valuation && received_termsheet) && { total_round_size, round_valuation, received_termsheet } },
    { "Fund Utilization": Boolean(fund_utilization_highlights && key_metrics) && { fund_utilization_highlights, key_metrics } },
    { "Current Traction": traction_highlights || false },
    { "Exit Strategy": exit_highlights || false },
    { Documents: documents || false },
    { Performance: performanceCompletion || false },
  ]
  // .filter((item) => Object.values(item)[0]); // Remove empty values

  const requiredFieldsLabels = requiredProgressFields.map(getProgressLabel);

  const dataWithLabelsObject = Object.assign({}, ...dataWithLabels);

  return { completion: completion || 0, round_id, dataWithLabels, requiredFieldsLabels, dataWithLabelsObject };
};

const getChangedFields = (currentValues, originalValues) => {
  return Object.keys(originalValues).reduce((changes, key) => {
    const formValue = currentValues[key];
    const originalValue = originalValues[key];

    // Simple comparison for primitives
    if (typeof formValue !== 'object' || formValue === null) {
      if (formValue !== originalValue) {
        changes[key] = { original: originalValue, current: formValue };
      }
    }
    // For objects, use JSON stringification
    else if (JSON.stringify(formValue) !== JSON.stringify(originalValue)) {
      changes[key] = { original: originalValue, current: formValue };
    }

    return changes;
  }, {});
};


const hasFormChanges = (currentValues, originalValues) => {
  // console.log(getChangedFields(currentValues, originalValues))
  return Object.keys(originalValues).some(key => {
    const formValue = currentValues[key];
    const originalValue = originalValues[key];

    // Simple comparison for primitives
    if (typeof formValue !== 'object' || formValue === null) {
      return formValue !== originalValue;
    }

    // For objects, use JSON stringification
    return JSON.stringify(formValue) !== JSON.stringify(originalValue);
  });
};

const profileProgress = [
  { id: 1, label: 'Below 25%', min: 0, max: 25, value: "below_25" },
  { id: 2, label: '25% to 50%', min: 25, max: 50, value: "25_to_50" },
  { id: 3, label: '50% to 75%', min: 50, max: 75, value: "50_to_75" },
  { id: 4, label: 'Above 75%', min: 75, max: 99, value: "above_75" },
  { id: 5, label: '100% Complete', min: 100, max: 100, value: "100_complete" }
];

const pageIndexCookie = () => {
  return Cookies.get("backrr_table_page")
}

export const getFormattedDate = (date) => {
  return moment(date).format("DD-MMM-YYYY")
}

// Sub-categories (sectors) are fetched by category NAME (`settings?config_type=<names>`),
// but the saved Investment Thesis only stores category IDs — `selectedCategories` (names)
// is a UI-only field the onboarding seed never writes. Derive the names from the ids so a
// prefilled thesis still loads its sectors. Returns a CSV of names, '' when unresolvable.
export const categoryIdsToNames = (categoryCsv, categories = []) => {
  if (!categoryCsv || !categories?.length) return '';
  return String(categoryCsv)
    .split(',')
    .filter(Boolean)
    .map((id) => categories.find((c) => c?._id === id)?.name)
    .filter(Boolean)
    .join(',');
};


const PERSON_NAME_DISALLOWED = /[^\p{L}\s'.-]/gu;

export const sanitizePersonName = (value) =>
  String(value ?? '').replace(PERSON_NAME_DISALLOWED, '');

export const encode = (data) => {
  return Base64.encode(data);
};

export const decode = (base64String) => {
  return Base64.decode(base64String);
};

const isVideoUrl = (url) => {
  const videoExtensions = ['mp4', 'webm', 'ogg', 'mov', 'avi', 'flv', 'wmv', 'mkv'];
  return videoExtensions.some(ext => url?.toLowerCase().includes(ext));
}



export {
  pageIndexCookie,
  chartsColorsTheme,
  getDeviceType,
  redirectTo,
  navigateToMain,
  singleSignOn,
  handleDateValues,
  getFileIcon,
  getFileCategory,
  containerRef,
  isRequired,
  popularCountrySort,
  linkedInTestURL,
  stringAvatar,
  capitalizedSentence,
  formatDealSource,
  handleCopyClick,
  interestOptions,
  notEmpty,
  formatAllowsNegativeCurrency,
  formatDecimal,
  formatCurrency,
  currencyAllowNegativeInputProps,
  formatAllowsNegativeDecimal,
  currencyInputProps,
  versionInputProps,
  versionCheck,
  amountInWords,
  handleFormSubmit,
  convertCurrencyAmountRange,
  getCurrencySymbol,
  getINRMarks,
  getUSDMarks,
  getMarks,
  technologyColor,
  consumerBrandColor,
  marketplaceColor,
  servicesColor,
  sectorAgnostic,
  colorArray,
  isTitleToShow,
  // investor_types,
  isSelectedInvestorGroup,
  websiteTestURL,
  downloadFile,
  // permissions 
  mergePermissions,
  defaultPermissions,
  adminDefaultPermissions,
  collaboratorDefaultPermissions,
  convertToPermissionOnly,
  adminPermission,
  hasPermission,
  hasPermissionForRoute,
  findFirstTrueValue,
  getFirstRoute,
  checkForAuthorization,
  setTokenForAuthorization,
  checkForUserType,
  setUserType,
  getTemplateKeyValue,
  navigateToDomain,
  hasFormChanges,
  profileProgress,
  formatProgressData,
  dealFlowStatuses,
  checkForEmail,
  getBucketUrl,
  extractMainDomain,
  isVideoUrl,
  delayHoursForView,
  shouldRecordView,
  getLastViewedAt,
  setLastViewedAt,
  getReelLastViewedAt,
  setReelLastViewedAt
  // InvestorsList
}